export type AppRoute =
  | { name: "home" }
  | { name: "auth" }
  | { name: "onboarding" }
  | { name: "friends" }
  | { name: "gjenger" }
  | { name: "gjeng"; id: string }
  | { name: "createPlan"; gjengId?: string }
  | { name: "pastPlans" }
  | { name: "plan"; id: string }
  | { name: "invite"; token: string }
  | { name: "planLink"; token: string }
  | { name: "gjengInvite"; token: string }
  | { name: "notifications" }
  | { name: "profile" }
  | { name: "userProfile"; userId: string }
  | { name: "legal"; page: "privacy" | "terms" };

export type PublicGuestLinkTarget =
  | { name: "planLink"; token: string }
  | { name: "gjengInvite"; token: string };

type RememberedGuestLinkTokens = {
  planToken: () => string | null;
};

const tokenPattern = /^[A-Za-z0-9_-]{10,160}$/;

export function parseRoute(pathname: string): AppRoute {
  const pathnameOnly = pathname.split(/[?#]/, 1)[0] || "/";
  const path = pathnameOnly.replace(/\/+$/, "") || "/";
  const parts = path.split("/").filter(Boolean);

  if (path === "/") return { name: "home" };
  if (path === "/auth" || path === "/auth/callback") return { name: "auth" };
  if (path === "/onboarding") return { name: "onboarding" };
  if (path === "/friends") return { name: "friends" };
  if (path === "/gjenger") return { name: "gjenger" };
  if (parts[0] === "gjenger" && parts[1]) return { name: "gjeng", id: parts[1] };
  if (path === "/plans/new") return { name: "createPlan" };
  // "Ny plan i gjengen": the create screen pre-targeted at a crew.
  if (parts[0] === "plans" && parts[1] === "new" && parts[2] === "gjeng" && parts[3]) {
    return { name: "createPlan", gjengId: parts[3] };
  }
  if (path === "/plans/past") return { name: "pastPlans" };
  if (parts[0] === "plans" && parts[1]) return { name: "plan", id: parts[1] };
  if (parts[0] === "i" && tokenPattern.test(parts[1] || "")) {
    return { name: "invite", token: parts[1] };
  }
  if (parts[0] === "p" && tokenPattern.test(parts[1] || "")) {
    return { name: "planLink", token: parts[1] };
  }
  if (parts[0] === "g" && tokenPattern.test(parts[1] || "")) {
    return { name: "gjengInvite", token: parts[1] };
  }
  if (path === "/notifications") return { name: "notifications" };
  if (path === "/profile") return { name: "profile" };
  if (parts[0] === "u" && parts[1]) return { name: "userProfile", userId: parts[1] };
  if (path === "/personvern") return { name: "legal", page: "privacy" };
  if (path === "/vilkar") return { name: "legal", page: "terms" };

  return { name: "home" };
}

export function isProtectedRoute(route: AppRoute): boolean {
  return route.name !== "auth" && route.name !== "legal";
}

export function authRedirectPath(route: AppRoute): string | null {
  if (route.name === "invite") return `/i/${encodeURIComponent(route.token)}`;
  if (route.name === "planLink") return `/p/${encodeURIComponent(route.token)}`;
  if (route.name === "gjengInvite") return `/g/${encodeURIComponent(route.token)}`;
  return null;
}

export function nextPathFromSearch(search: string): string | null {
  const next = new URLSearchParams(search).get("next");
  if (!next?.startsWith("/")) return null;
  if (next.startsWith("//")) return null;

  const path = next.split(/[?#]/, 1)[0] || "/";
  const route = parseRoute(path);
  return route.name === "invite" || route.name === "planLink" || route.name === "gjengInvite"
    ? path
    : null;
}

export function resolveGuestPublicLinkTarget(
  route: AppRoute,
  remembered: RememberedGuestLinkTokens
): PublicGuestLinkTarget | null {
  if (route.name === "planLink") return { name: "planLink", token: route.token };
  if (route.name === "gjengInvite") return { name: "gjengInvite", token: route.token };

  const planToken = remembered.planToken();
  if (planToken) return { name: "planLink", token: planToken };

  return null;
}
